import type { RequiredEnvironmentRegistry } from '../types/RequiredEnvironmentRegistry';
import type { Result, AppError } from '../../error/types';
import { ok, err } from '../../error/functions';

/**
 * @group Classes
 * 
 * @summary <blockquote>Manages the required environment registry for the application</blockquote>
 * 
 * Provides a centralized registry for required environment variables per app type and environment.
 * Similar to how Logger/ResourceConfig works - apps set the registry once at initialization,
 * and shared utilities access it via the getter.
 * 
 * @example
 * ```typescript
 * // In apps/web/app/layout.tsx
 * import { RequiredEnvironmentRegistryManager } from '@shared/utils';
 * import { WEB_REQUIRED_ENVIRONMENT_VALUES } from '@/config/WEB_REQUIRED_ENVIRONMENT_VALUES';
 * 
 * RequiredEnvironmentRegistryManager.set({
 *   web: WEB_REQUIRED_ENVIRONMENT_VALUES,
 *   mobile: { development: [], preview: [], production: [] }
 * });
 * ```
 */
export class RequiredEnvironmentRegistryManager {
  private static _registry: RequiredEnvironmentRegistry | null = null;

  /**
   * Sets the required environment registry for the application.
   * Call this once at app initialization.
   */
  static set(registry: RequiredEnvironmentRegistry): void {
    RequiredEnvironmentRegistryManager._registry = registry;
  }

  /**
   * @internal
   * Gets the required environment registry.
   * Used internally by validation functions. Apps should not call this directly.
   */
  static get(): Result<RequiredEnvironmentRegistry, AppError> {
    if (RequiredEnvironmentRegistryManager._registry === null) {
      return err({
        errorCode: 'REGISTRY_NOT_INITIALIZED',
        errorMessage: 'Required environment registry not initialized. Call RequiredEnvironmentRegistryManager.set() at app initialization.'
      });
    }
    return ok(RequiredEnvironmentRegistryManager._registry);
  }
}

